import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import toast from "react-hot-toast";
import { useAddVendorMutation } from "../../toolkitServices/vendorApi";
import { indianStates } from "../../utils/helpers";

const INITIAL_FORM = {
  vendorName: "",
  companyName: "",
  contact: "",
  email: "",
  gst: "",
  vendorType: "party",
  addressLine1: "",
  city: "",
  pincode: "",
  state: "Maharashtra",
  country: "india",
};

const QuickAddVendorModal = ({ open, onClose, onAdded }) => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [addVendor, { isLoading }] = useAddVendorMutation();
  const nameRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setForm(INITIAL_FORM);
      return;
    }

    const id = setTimeout(() => nameRef.current?.focus(), 0);
    return () => clearTimeout(id);
  }, [open]);

  if (!open) return null;

  const update = (name, value) => setForm((prev) => ({ ...prev, [name]: value }));

  const onSubmit = async (event) => {
    event?.preventDefault();

    const vendorName = form.vendorName.trim();
    if (!vendorName) {
      toast.error("Vendor name is required");
      nameRef.current?.focus();
      return;
    }

    if (form.pincode && !/^\d{6}$/.test(form.pincode)) {
      toast.error("Pincode must be a 6-digit number");
      return;
    }

    const payload = {
      ...form,
      vendorName,
      companyName: form.companyName.trim(),
      contact: form.contact.trim(),
      email: form.email.trim().toLowerCase(),
      gst: form.gst.trim().toUpperCase(),
      addressLine1: form.addressLine1.trim(),
      city: form.city.trim(),
      pincode: form.pincode.trim(),
      state: form.state.trim(),
      country: form.country.trim(),
      isActive: true,
    };

    try {
      const response = await addVendor(payload).unwrap();
      const created = response?.data || response;
      toast.success("Vendor added successfully");
      onAdded?.(created);
      onClose?.();
    } catch (error) {
      toast.error(
        error?.data?.msg || error?.data?.message || error?.error || "Failed to add vendor",
      );
    }
  };

  const inputClass =
    "w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#9481ce]";

  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/40 p-3">
      <motion.div
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-lg rounded-xl bg-white p-4 shadow-2xl"
      >
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-base font-semibold text-slate-800">Quick Add Vendor</h3>
          <button type="button" onClick={onClose} className="rounded-md border px-2 py-1 text-xs hover:bg-slate-50">
            Close
          </button>
        </div>

        <form
          onSubmit={onSubmit}
          onKeyDownCapture={(event) => {
            if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "s") {
              event.preventDefault();
              event.stopPropagation();
              if (!isLoading) onSubmit(event);
            }
          }}
          className="max-h-[75vh] space-y-3 overflow-y-auto pr-1"
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="text-xs font-medium text-slate-600">
              Vendor Name <span className="text-red-500">*</span>
              <input ref={nameRef} value={form.vendorName} onChange={(e) => update("vendorName", e.target.value)} className={`${inputClass} mt-1`} />
            </label>
            <label className="text-xs font-medium text-slate-600">
              Company Name
              <input value={form.companyName} onChange={(e) => update("companyName", e.target.value)} className={`${inputClass} mt-1`} />
            </label>
            <label className="text-xs font-medium text-slate-600">
              Contact
              <input value={form.contact} onChange={(e) => update("contact", e.target.value.replace(/\D/g, ""))} inputMode="numeric" className={`${inputClass} mt-1`} />
            </label>
            <label className="text-xs font-medium text-slate-600">
              Email
              <input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className={`${inputClass} mt-1`} />
            </label>
            <label className="text-xs font-medium text-slate-600">
              GST
              <input value={form.gst} onChange={(e) => update("gst", e.target.value)} className={`${inputClass} mt-1 uppercase`} />
            </label>
            <label className="text-xs font-medium text-slate-600">
              Vendor Type
              <select value={form.vendorType} onChange={(e) => update("vendorType", e.target.value)} className={`${inputClass} mt-1`}>
                <option value="party">Party</option>
                <option value="supplier">Supplier</option>
                <option value="other">Other</option>
              </select>
            </label>
          </div>

          <div className="space-y-3 rounded-lg border bg-slate-50/50 p-3">
            <div className="text-xs font-semibold text-slate-700">Address</div>
            <input value={form.addressLine1} onChange={(e) => update("addressLine1", e.target.value)} placeholder="Address line" className={inputClass} />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <input value={form.city} onChange={(e) => update("city", e.target.value)} placeholder="City" className={inputClass} />
              <input value={form.pincode} onChange={(e) => update("pincode", e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" placeholder="6-digit pincode" className={inputClass} />
              <select value={form.state} onChange={(e) => update("state", e.target.value)} className={inputClass}>
                <option value="">Select State</option>
                {indianStates.map((state) => <option key={state} value={state}>{state}</option>)}
              </select>
              <select value={form.country} onChange={(e) => update("country", e.target.value)} className={inputClass}>
                <option value="india">India</option>
              </select>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded-md bg-gray-200 px-3 py-1.5 text-sm">Cancel</button>
            <button type="submit" disabled={isLoading} className="rounded-md bg-[#6b59c6] px-3 py-1.5 text-sm text-white disabled:opacity-60">
              {isLoading ? "Saving..." : "Save & Select"}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default QuickAddVendorModal;
